import React, { SyntheticEvent, useEffect, useState } from 'react';
import TwAutocomplete, {
  RenderOption,
  TwAutocompleteOption
} from '../TwAutocomplete/TwAutocomplete';
import {
  useGetAllEquipmentByTokenQuery,
  useSearchEquipmentsQuery
} from '../../redux/api/equipment/equipmentApi';
import { Equipment, EquipmentType } from '../../redux/api/equipment/equipment.model';
import StringUtils from '../../utils/stringUtils';
import CreateEquipment from '../../pages/Equipment/CreateEquipment/CreateEquipment';
import { SxProps } from '@mui/material';
import useEquipmentSearch from '../../hooks/useEquipmentSearch';

type EquipmentSelectorProps = {
  label?: string;
  isEquipmentPreselected: boolean;
  selectedEquipment: TwAutocompleteOption | null | undefined;
  hasError: boolean;
  onChange: (equipment: TwAutocompleteOption | null) => void;
  setSelectedEquipment: (equipment: TwAutocompleteOption | null) => void;
  setOpenEquipmentModal?: (open: boolean) => void;
  fromDocManager?: boolean;
  isDriverLink?: boolean;
  readOnly?: boolean;
  isDriver?: boolean;
  noActions?: boolean;
  token?: string;
  sx?: SxProps;
  equipmentType?: EquipmentType;
};

const TwEquipmentSelector: React.FC<EquipmentSelectorProps> = ({
  label,
  isEquipmentPreselected,
  selectedEquipment,
  hasError,
  setSelectedEquipment,
  onChange,
  setOpenEquipmentModal,
  fromDocManager = false,
  isDriverLink = false,
  readOnly = false,
  isDriver = false,
  noActions = false,
  token,
  sx = {},
  equipmentType
}): JSX.Element => {
  const [equipmentOptions, setEquipmentOptions] = React.useState<TwAutocompleteOption[]>([]);
  const [openCreateEquipment, setOpenCreateEquipment] = useState<boolean>(false);
  const [equipmentFilterLabels, setEquipmentFilterLabels, fetchEquipmentsForFilter] =
    useEquipmentSearch(equipmentType);
  const [disableNoOption, setDisableNoOption] = useState<boolean>(true);

  // Fetch equipment list by token
  const { data: equipmentListByToken = { equipments: [], total: 0 } } =
    useGetAllEquipmentByTokenQuery(token || '', {
      // TODO: Make this api take search param so we dont fetch all equipments for the tenant
      skip: !isDriverLink
    });

  // Transform equipment list
  const transformEquipmentList = (equipments: Equipment[]): TwAutocompleteOption[] => {
    return equipments.map((equipment) => ({
      id: equipment.id,
      value: equipment.id,
      label: equipment.name,
      subLabel: StringUtils.capitalizeFirstLetter(equipment.type.toLowerCase())
    }));
  };

  // Open create equipment modal
  const handleOpenCreateEquipment = (): void => {
    setOpenEquipmentModal && setOpenEquipmentModal(true);
    setOpenCreateEquipment(true);
  };

  // Close create equipment modal
  const handleCloseCreateEquipment = (): void => {
    setOpenEquipmentModal && setOpenEquipmentModal(false);
    setOpenCreateEquipment(false);
  };

  // Create equipment success
  const handleCreateEquipmentSuccess = (equipment: Equipment): void => {
    const equipmentOption = {
      id: equipment.id,
      value: equipment.id,
      label: equipment.name,
      subLabel: equipment.type
    };
    setSelectedEquipment(equipmentOption);
    onChange(equipmentOption);
  };

  useEffect(() => {
    if (token && equipmentListByToken.equipments.length > 0) {
      setEquipmentOptions(transformEquipmentList(equipmentListByToken.equipments));
      return;
    }

  }, [JSON.stringify(equipmentListByToken.equipments)]);

  return (
    <>
      <TwAutocomplete
        label={label}
        name="equipment"
        filterOptions={(x: any): any => x}
        renderOption={(props, option): React.ReactNode => {
          return <RenderOption key={option.id} props={props} option={option} />;
        }}
        optionList={token ? equipmentOptions : equipmentFilterLabels}
        value={selectedEquipment || null}
        onChange={(_, newValue: TwAutocompleteOption | null): void => {
          const newClient = newValue ? ({ ...newValue } as TwAutocompleteOption) : null;
          newClient
            ? (newClient.label = `${newValue?.label}${
                !equipmentType ? ` (${newValue?.subLabel})` : ''
              }`)
            : null;
          setSelectedEquipment(newClient);
          onChange(newClient);
        }}
        onInputChange={(event: SyntheticEvent<Element, Event>, value: string | null): void => {
          if (!token) {
            if (value === '') {
              setEquipmentFilterLabels([]);
              setDisableNoOption(true);
            } else {
              if (value) {
                fetchEquipmentsForFilter(value);
                setDisableNoOption(false);
              }
            }
          }
        }}
        listAction={
          (!isDriverLink && !isDriver) || !noActions
            ? {
                label: `New ${equipmentType ? equipmentType.toLowerCase() : 'equipment'}`,
                action: (): void => {
                  handleOpenCreateEquipment();
                }
              }
            : undefined
        }
        error={hasError}
        sx={sx}
        disabled={isEquipmentPreselected}
        readonly={readOnly}
        showEndAdornment={!readOnly}
        enableClearIcon={true}
        disableNoOption={disableNoOption}
      />

      {openCreateEquipment && (
        <CreateEquipment
          open={openCreateEquipment}
          defaultType={equipmentType}
          onClose={handleCloseCreateEquipment}
          onSuccess={handleCreateEquipmentSuccess}
          fromDocManager={fromDocManager}
        />
      )}
    </>
  );
};

export default TwEquipmentSelector;
